import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useVelocity, useTransform } from "framer-motion";

export const CustomCursor = () => {
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [visible, setVisible] = useState(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 600, damping: 40, mass: 0.6 });
  const sy = useSpring(y, { stiffness: 600, damping: 40, mass: 0.6 });

  const vx = useVelocity(sx);
  const vy = useVelocity(sy);
  const rotate = useTransform([vx, vy], ([a, b]) => (Math.hypot(a, b) > 50 ? (Math.atan2(b, a) * 180) / Math.PI : 0));
  const stretch = useTransform([vx, vy], ([a, b]) => Math.min(Math.hypot(a, b) / 4500, 0.45));
  const scaleX = useTransform(stretch, (s) => 1 + s);
  const scaleY = useTransform(stretch, (s) => 1 - s * 0.55);

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)");
    if (!fine.matches) return;
    setEnabled(true);
    document.documentElement.classList.add("custom-cursor");

    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
    };
    const over = (e) => setHovering(!!e.target.closest("a, button, [role='button'], input, textarea, select"));
    const down = () => setPressed(true);
    const up = () => setPressed(false);
    const leave = () => setVisible(false);

    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    document.documentElement.addEventListener("mouseleave", leave);
    return () => {
      document.documentElement.classList.remove("custom-cursor");
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      document.documentElement.removeEventListener("mouseleave", leave);
    };
  }, [x, y]);

  if (!enabled) return null;

  return (
    <motion.div
      aria-hidden="true"
      data-testid="custom-cursor"
      className={`fixed top-0 left-0 z-[100] pointer-events-none ${hovering ? "" : "mix-blend-difference"}`}
      style={{ x: sx, y: sy, opacity: visible ? 1 : 0 }}
    >
      <motion.div style={{ rotate, scaleX, scaleY }} className="w-0 h-0 flex items-center justify-center">
        <motion.div
          data-testid="custom-cursor-dot"
          animate={{
            width: hovering ? 64 : 22,
            height: hovering ? 64 : 22,
            scale: pressed ? 0.85 : 1,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 28 }}
          className={`shrink-0 rounded-full ${hovering ? "bg-[#FFB6C1]/60" : "bg-white"}`}
        />
      </motion.div>
    </motion.div>
  );
};
